import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { trackFailure, trackOutcome } from '@/analytics/events';
import { AuthContext, type AuthContextValue } from '@/context/auth-context';
import { auth } from '@/lib/firebase';

const googleProvider = new GoogleAuthProvider();

googleProvider.setCustomParameters({
  prompt: 'select_account',
});

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);

  useEffect(() => {
    let sessionVersion = 0;

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      const activeVersion = ++sessionVersion;

      setUser(firebaseUser);

      if (!firebaseUser) {
        setIsAdmin(false);
        setIsLoadingAuth(false);
        return;
      }

      void firebaseUser
        .getIdTokenResult()
        .then((token) => {
          if (activeVersion !== sessionVersion) {
            return;
          }

          setIsAdmin(token.claims.admin === true);
          setIsLoadingAuth(false);
        })
        .catch(() => {
          if (activeVersion !== sessionVersion) {
            return;
          }

          setIsAdmin(false);
          setIsLoadingAuth(false);
        });
    });

    return () => unsubscribe();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoadingAuth,
      isAuthenticated: Boolean(user),
      isAdmin,
      signInWithGoogle: async () => {
        trackOutcome('auth_sign_in', 'attempted', {
          method: 'google',
        });

        try {
          await signInWithPopup(auth, googleProvider);
          trackOutcome('auth_sign_in', 'succeeded', {
            method: 'google',
          });
        } catch (error) {
          trackFailure('auth_sign_in', error, {
            method: 'google',
          });
          throw error;
        }
      },
      logout: async () => {
        await signOut(auth);
      },
    }),
    [isAdmin, isLoadingAuth, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
